import * as React from "react";
import { clsx } from "clsx";
import { CSSTransition } from "react-transition-group";
import "debounce";
import "tabbable";
import { useResponsiveProps } from "../helpers/helper/hooks/use-responsive-props.mjs";
import { Icon } from "../icon/icon/icon.mjs";
import { useRouter } from "../helpers/router/use-router.mjs";
import { Skeleton } from "../skeleton/skeleton/skeleton.mjs";
import { SkeletonItem } from "../skeleton/skeleton-item/skeleton-item.mjs";
import { Spinner } from "../spinner/spinner.mjs";
const Button = React.forwardRef(function Button2(props, ref) {
  const { responsive, onClick: propOnClick, ...rest } = props;
  const { navigate } = useRouter({ onClick: propOnClick });
  return responsive ? /* @__PURE__ */ React.createElement(ButtonResponsive, { ...rest, ref, responsive, onClick: navigate }) : /* @__PURE__ */ React.createElement(ButtonInner, { ...rest, buttonRef: ref, onClick: navigate });
});
const ButtonResponsive = React.forwardRef(function ButtonResponsive2(props, ref) {
  const { responsive, ...rest } = props;
  const responsiveProps = useResponsiveProps({
    responsive
  });
  return /* @__PURE__ */ React.createElement(ButtonInner, { buttonRef: ref, ...rest, ...responsiveProps });
});
class ButtonInner extends React.PureComponent {
  constructor() {
    super(...arguments);
    this.handleClick = (event) => {
      event.stopPropagation();
      if (this.props.disabled) {
        event.preventDefault();
        return;
      }
      if (!this.props.external && this.props.onClick) {
        this.props.onClick(event);
      }
    };
  }
  getClassNames() {
    var _a;
    const isUnderlined = (_a = this.props.isUnderlined) != null ? _a : true;
    return clsx(
      "btn",
      {
        ["btn--".concat(this.props.type)]: !!this.props.type,
        ["btn--".concat(this.props.size)]: !!this.props.size,
        ["btn--icon-only"]: !!this.props.icon,
        ["btn--variant-".concat(this.props.variant)]: !!this.props.variant,
        ["btn--link"]: !!this.props.link,
        ["btn--underlined"]: !!this.props.link && !!isUnderlined,
        ["btn--block"]: !!this.props.block,
        ["is-active"]: !!this.props.isActive,
        ["is-loading"]: !!this.props.isLoading,
        ["btn--vertical-align-".concat(this.props.verticalAlign)]: !!this.props.verticalAlign,
        ["btn--with-arrow-right-icon"]: this.props.iconRight === "#arrow-right" && !this.props.link
      },
      this.props.className
    );
  }
  getIcon(location, modifiers, size) {
    return /* @__PURE__ */ React.createElement(
      "span",
      {
        className: clsx("btn__icon-wrapper", "btn__icon-wrapper--".concat(location), {
          ["btn__icon-wrapper--".concat(size)]: !!size
        })
      },
      /* @__PURE__ */ React.createElement(
        Icon,
        {
          modifiers,
          size,
          className: clsx(
            "btn__icon",
            "btn__icon--".concat(location),
            {
              ["btn__icon--custom-size"]: (location === "left" && this.props.iconLeftSize || location === "right" && this.props.iconRightSize || location === "center" && this.props.iconSize) && this.props.link
            },
            this.props.iconClass
          )
        }
      )
    );
  }
  renderContent() {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, this.props.isLoading && /* @__PURE__ */ React.createElement(CSSTransition, { in: this.props.isLoading, timeout: 300, classNames: "fade", unmountOnExit: true }, /* @__PURE__ */ React.createElement(Spinner, { className: "btn__spinner" })), /* @__PURE__ */ React.createElement("span", { className: "btn__inner" }, this.props.iconLeft && this.getIcon("left", this.props.iconLeft, this.props.iconLeftSize), this.props.icon && this.getIcon("center", this.props.icon, this.props.iconSize), /* @__PURE__ */ React.createElement("span", { className: "btn__text" }, this.props.text), this.props.iconRight && this.getIcon("right", this.props.iconRight, this.props.iconRightSize)));
  }
  isAnchor(props) {
    return "url" in props;
  }
  isSpan(props) {
    return props.element === "span";
  }
  renderAnchor(props) {
    const {
      url,
      element,
      attributes,
      onClick,
      target,
      buttonRef,
      isActive,
      isLoading,
      external,
      ariaLabel,
      responsive,
      type,
      size,
      className,
      text,
      variant,
      link,
      isUnderlined,
      block,
      icon,
      iconLeft,
      iconRight,
      iconSize,
      iconLeftSize,
      iconRightSize,
      iconClass,
      verticalAlign,
      theme,
      ...rest
    } = props;
    return /* @__PURE__ */ React.createElement(
      "a",
      {
        ...rest,
        ...attributes,
        className: this.getClassNames(),
        ref: buttonRef,
        "aria-label": ariaLabel,
        onClick: this.handleClick,
        href: url,
        target,
        "data-theme": theme
      },
      this.renderContent()
    );
  }
  renderSpan(props) {
    const {
      element,
      attributes,
      onClick,
      target,
      buttonRef,
      isActive,
      isLoading,
      external,
      ariaLabel,
      responsive,
      type,
      size,
      className,
      text,
      variant,
      link,
      isUnderlined,
      block,
      icon,
      iconLeft,
      iconRight,
      iconSize,
      iconLeftSize,
      iconRightSize,
      iconClass,
      verticalAlign,
      tabIndex,
      theme,
      ...rest
    } = props;
    return /* @__PURE__ */ React.createElement(
      "span",
      {
        ...rest,
        ...attributes,
        className: this.getClassNames(),
        ref: buttonRef,
        "aria-label": ariaLabel,
        "data-theme": theme
      },
      this.renderContent()
    );
  }
  renderButton(props) {
    var _a;
    const {
      element,
      attributes,
      onClick,
      target,
      buttonRef,
      isActive,
      isLoading,
      external,
      ariaLabel,
      responsive,
      type,
      size,
      className,
      text,
      variant,
      link,
      isUnderlined,
      block,
      icon,
      iconLeft,
      iconRight,
      iconSize,
      iconLeftSize,
      iconRightSize,
      iconClass,
      verticalAlign,
      disabled,
      theme,
      ...rest
    } = props;
    return /* @__PURE__ */ React.createElement(
      "button",
      {
        ...rest,
        ...attributes,
        className: this.getClassNames(),
        ref: buttonRef,
        "aria-label": ariaLabel,
        onClick: this.handleClick,
        type: (_a = attributes == null ? void 0 : attributes.type) != null ? _a : "button",
        "aria-disabled": disabled,
        "data-theme": theme
      },
      this.renderContent()
    );
  }
  renderSkeleton(props) {
    const { skeleton } = props;
    return /* @__PURE__ */ React.createElement(Skeleton, { label: skeleton.label, theme: this.props.theme }, /* @__PURE__ */ React.createElement(SkeletonItem, { className: this.getClassNames(), width: skeleton.width }));
  }
  render() {
    const { ...props } = this.props;
    if ("skeleton" in props) {
      return this.renderSkeleton(props);
    }
    if (this.isSpan(props)) {
      return this.renderSpan(props);
    }
    if (this.isAnchor(props)) {
      if (props.disabled) {
        return this.renderButton(props);
      }
      return this.renderAnchor(props);
    }
    return this.renderButton(props);
  }
}
export {
  Button
};
